import './say.css';
import { speakDefaults, speakEspeak, speakPhonemes } from '../../speak/speakPhonemes';
import { SpeakVariant, meSpeakOptions } from '../../speak/meSpeak';
import { SupportedLanguage, SupportedAccent, AllRules } from '../../ruleSets/allRules';
import { IpaType, primaryStress, secondaryStress } from '../../phonemes/ipa';
import { EnglishAccentType } from '../../ruleSets/accentRules';
import { SpanishAccentType } from '../../ruleSets/spanishToIpa';
import { PhoneLengths } from '../../rules/lengthRules';

const defaultEnglishAccent: EnglishAccentType = 'Western US';
const defaultSpanishAccent: SpanishAccentType = 'Mexico';

interface SayProps {
  /** what to show and optionally say, could be "English",
   * "/IPA/" then run accent specific length rules, "[IPA]" as-is, or "[[synth]]" */
  readonly children: string;
  /** specify this if you want to say something other than what's shown */
  readonly say?: string;

  /** which language to use for interpreting input, default English */
  readonly language?: SupportedLanguage;
  /** which accent to apply to input, default is based on language */
  readonly accent?: SupportedAccent;
  /** override language/accent or specify when none */
  readonly lengthRules?: PhoneLengths;

  /** name of the espeak variant to use, e.g., 'fiona' | 'martin' */
  readonly variant?: SpeakVariant;
  /** voice pitch, default: 50 */
  readonly pitch?: number;
  /** words per minute, default: 150 */
  readonly speed?: number;

  /** color used for drawing text */
  readonly textColor?: string;

  /** called when speaking starts */
  readonly onStart?: () => void;
  /** called when speaking is done */
  readonly onDone?: () => void;
}

/** shows text you can click on to hear it spoken */
export function Say(props: SayProps) {
  const options = makeOptions(props);

  function onClick() {
    if (props.onStart) {
      props.onStart();
    }
    const toSay = props.say ?? props.children;
    convertAndSpeak(toSay, options, props.language, props.accent, props.lengthRules, props.onDone);
  }

  const style: React.CSSProperties = props.children.includes(' ')
    ? { color: props.textColor }
    : { color: props.textColor, whiteSpace: "nowrap" };

  const playButton = '\u23f5';
  return (<span onClick={onClick} className='say' style={style}>
    {playButton}
    {props.children}
  </span>);
}

/** assemble speak-options from defaults & props */
function makeOptions(props: SayProps): meSpeakOptions {
  const pitch = props.pitch ?? 50;
  const speed = props.speed ?? 150;
  let options: meSpeakOptions = { ...speakDefaults, pitch, speed };
  if (props.variant) {
    options = { ...options, variant: props.variant };
  }
  return options;
}

/** speak /IPA-noLength/ or [IPA-asis] or [[synth]], otherwise convert text to given language & accent */
function convertAndSpeak(
  text: string, options: meSpeakOptions, language?: SupportedLanguage, accent?: SupportedAccent, lengthRulesOverride?: PhoneLengths, onDone?: () => void
) {
  // check for [[synth]]
  const isSynth = text.startsWith('[[');
  if (isSynth) {
    speakEspeak(text.substring(2, text.length - 2), options, onDone);
    return;
  }

  const { ipa, lengthRules } = toLanguageAccent(text, language, accent);
  speakPhonemes(ipa, options, lengthRulesOverride ?? lengthRules, onDone);
}

/**
 * convert text to IPA using the specified language & accent,
 * /text/ is interpreted as IPA that may need accent-specific lengths added,
 * [text] is IPA to use as-is
 * @returns { ipa, phoneme-length-rules }
 */
function toLanguageAccent(
  text: string, language?: SupportedLanguage, accent?: SupportedAccent
): { ipa: IpaType, lengthRules?: PhoneLengths } {
  const isBroad = text[0] === '/' && text[1] !== '|'; // `/ipa/`, not `/|rising-tone`
  const isNarrow = text[0] === '[';

  // validate language & accent
  language = language ?? 'English';
  accent = language === 'English' ? accent ?? defaultEnglishAccent
    : language === 'Spanish' ? accent ?? defaultSpanishAccent
      : accent;
  // get lengthening rules
  const lengthRules = AllRules.get().getAddPhonemeLength(language, accent);

  // check for /IPA/ or [IPA]
  if (isBroad || isNarrow) {
    // interpret as IPA and speak
    const trim1 = text.substring(1);
    const last = trim1[trim1.length - 1];
    let ipa = last === '/' || last === ']' ? trim1.substring(0, trim1.length - 1) : trim1;
    if (isBroad && !ipa.includes(primaryStress) && !ipa.includes(secondaryStress)) {
      // sounds better if there's at least one stress indicator,
      // but we'll only add it for a broad transcription
      ipa = `${primaryStress}${ipa}`;
    }
    return isNarrow ? { ipa } : { ipa, lengthRules };
  }

  // interpret as the input language & convert to IPA based on accent
  const { afterRules } = AllRules.get().convert(text, language, accent);
  return { ipa: afterRules, lengthRules };
}
